import { useState, useEffect, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../providers/firebase/firebase';
import { nsToDate, dateToNs } from './timestampUtils';
import type { AccountPortfolio } from '../types';
import type { Interval } from '../pages/portfolio/intervalDefaults';

type RawAggRow = {
  valuation: AccountPortfolio['valuation'];
  timestamp: string;
};

type RawPerAccountRow = Omit<AccountPortfolio, 'timestamp'> & { timestamp: string };

interface CombinedResponse {
  rows: RawAggRow[];
  latestPerAccount: RawPerAccountRow[];
  truncated: boolean;
}

export function useCombinedPortfolios(
  accountIds: string[],
  from: Date,
  to: Date,
  interval: Interval,
): {
  data: AccountPortfolio[];
  perAccount: AccountPortfolio[];
  loading: boolean;
  error: Error | null;
} {
  const [data, setData] = useState<AccountPortfolio[]>([]);
  const [perAccount, setPerAccount] = useState<AccountPortfolio[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const callIdRef = useRef(0);

  const paramsJson = JSON.stringify({
    accountIds,
    from: dateToNs(from),
    to: dateToNs(to),
    interval,
  });

  useEffect(() => {
    if (accountIds.length === 0) {
      setData([]);
      setPerAccount([]);
      setLoading(false);
      return;
    }

    const callId = ++callIdRef.current;
    setLoading(true);
    setError(null);

    const callable = httpsCallable<Record<string, unknown>, CombinedResponse>(
      functions,
      'loadAccountPortfolios',
    );

    callable({
      accountIds,
      from: dateToNs(from),
      to: dateToNs(to),
      interval,
    })
      .then((result) => {
        if (callId !== callIdRef.current) return;

        const aggRows = result.data.rows
          .map((r) => ({
            account_id: '__all__',
            asset_data: { distribution: {}, balances: {} },
            valuation: r.valuation,
            meta_data: {},
            timestamp: nsToDate(r.timestamp),
          }));

        const latestRows = (result.data.latestPerAccount || [])
          .map((r) => ({
            ...r,
            timestamp: nsToDate(r.timestamp),
          }));

        setData(aggRows);
        setPerAccount(latestRows);
      })
      .catch((err) => {
        if (callId !== callIdRef.current) return;
        console.error('useCombinedPortfolios failed:', err);
        setError(err);
        setData([]);
        setPerAccount([]);
      })
      .finally(() => {
        if (callId !== callIdRef.current) return;
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsJson]);

  return { data, perAccount, loading, error };
}
